import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import { AddCircleOutline, CancelOutlined } from '@material-ui/icons';
import SelectDropDown from '../_shared/selectDropdown';
import DuesCycleSelector from '../_shared/DuesCycleSelector';
import DuesOptionSelector, {
  ANY_OPTION_VALUE,
} from '../_shared/DuesOptionSelector';
import Container from '../_shared/container';
import Text from '../_shared/text';
import Wrap from '../_shared/wrap';
import Bold from '../_shared/bold';
import Content from '../../../_shared/content';
import FitContent from '../_shared/fitContent';
import Space from '../_shared/space';
import {
  addFilter,
  removeFilter,
  toast,
} from '../../../../../../../../../redux/action';
import { selectedOrganizationSelector } from '../../../../../../../../../redux/selector';
import {
  getDuesCycles,
  getDuesOptions,
  duesCycleLabel,
} from '../../../../../../../../../redux/services/dues';

// Selected options render one per row below their cycle (product direction,
// NR-1464 spec section 12 item 2) - option names are too long for an inline
// comma-separated list. Container is a WRAPPING inline-flex row, not a
// column: anything that must start on its own line has to claim the full row
// width, or the next sibling slots in beside it.
const EntryBlock = styled.span`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Breathing room between committed entries: option rows are tight, so
     entries with them need more than the Any-only single line. */
  padding-bottom: ${({ hasOptionRows }) => (hasOptionRows ? '16px' : '8px')};
`;

const OptionRow = styled.span`
  display: flex;
  align-items: center;
  padding-left: 35px;
  /* The natural line spacing under the entry's first line reads too tall;
     pull the FIRST option up to roughly halve that gap. */
  margin-top: ${({ tightenTop }) => (tightenTop ? '-4px' : 0)};
`;

const PickedOptionRow = styled(Content)`
  width: 100%;
`;

// Normalize the selector's working array into the stored shape: a sole "Any"
// (or empty) becomes the scalar '*'; otherwise the array of specific GUIDs.
const normalizeOptions = optionsValue => {
  const specifics = optionsValue.filter(v => v !== ANY_OPTION_VALUE);
  return specifics.length ? specifics : ANY_OPTION_VALUE;
};

// Order-independent equality for a stored duesOptions value ('*' or GUID array).
const sameOptionSet = (a, b) => {
  const toSorted = v =>
    v === ANY_OPTION_VALUE ? [ANY_OPTION_VALUE] : [...v].sort();
  const av = toSorted(a);
  const bv = toSorted(b);
  return av.length === bv.length && av.every((x, i) => x === bv[i]);
};

const BalanceByDuesCycle = ({ filterName, checked, selectedFilters }) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(selectedOrganizationSelector).id;

  const [entries, setEntries] = useState(selectedFilters || []);

  // Data lists owned by this parent and passed down to the (presentational)
  // selectors. GenerateRow uses unmountOnExit, so this remounts and re-fetches
  // each time the filter is expanded; entries persist in Redux.
  const [cyclesList, setCyclesList] = useState([]);
  const [optionsList, setOptionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Input state for the entry being built.
  const [cycle, setCycle] = useState('');
  const [optionsValue, setOptionsValue] = useState([ANY_OPTION_VALUE]);
  const [paymentStatus, setPaymentStatus] = useState('');

  // GUID -> name maps for rendering saved rows (entries store GUIDs only).
  const cycleNameByGuid = useMemo(
    () => Object.fromEntries(cyclesList.map(c => [c.value, c.label])),
    [cyclesList]
  );
  const optionNameByGuid = useMemo(
    () => Object.fromEntries(optionsList.map(o => [o.value, o.label])),
    [optionsList]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setFetchError(false);
      const [cyclesRes, optionsRes] = await Promise.all([
        getDuesCycles(organizationId),
        getDuesOptions(organizationId),
      ]);
      if (!active) return;
      if (!Array.isArray(cyclesRes) || !Array.isArray(optionsRes)) {
        setFetchError(true);
        setLoading(false);
        dispatch(
          toast(
            'error',
            'Failed to load dues cycles and options. Please try again.'
          )
        );
        return;
      }
      setCyclesList(
        cyclesRes.map(c => ({ value: c.AMS_UID, label: duesCycleLabel(c) }))
      );
      setOptionsList(
        optionsRes.map(o => ({ value: o.AMS_UID, label: o.name }))
      );
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [organizationId]);

  useEffect(() => {
    if (entries.length) {
      dispatch(addFilter({ [filterName]: entries }));
    } else if (
      entries.length === 0 &&
      selectedFilters &&
      selectedFilters.length !== 0
    ) {
      dispatch(removeFilter(filterName));
    }
  }, [entries]);

  useEffect(() => {
    if (!checked && entries.length) {
      dispatch(removeFilter(filterName));
    }
  }, [checked]);

  const inputsDisabled = loading || fetchError;

  // Specific GUIDs currently picked in the entry being built ('Any' excluded).
  const pickedOptions = optionsValue.filter(v => v !== ANY_OPTION_VALUE);

  const handleRemoveOption = guid => {
    const next = pickedOptions.filter(v => v !== guid);
    setOptionsValue(next.length ? next : [ANY_OPTION_VALUE]);
  };

  const handlePaymentStatusChange = ({ target }) => {
    setPaymentStatus(target.value === 'Paid' ? 'paid' : 'unpaid');
  };

  const handleAdd = () => {
    const errorMessage = [];
    if (!cycle) {
      errorMessage.push('Please select a dues cycle.');
    }
    if (!paymentStatus) {
      errorMessage.push('Please select paid or unpaid.');
    }
    if (errorMessage.length) {
      dispatch(toast('error', errorMessage.join('\n')));
      return;
    }

    const newOptions = normalizeOptions(optionsValue);
    const isDuplicate = entries.some(
      entry =>
        entry.duesCycle === cycle &&
        entry.paymentStatus === paymentStatus &&
        sameOptionSet(entry.duesOptions, newOptions)
    );
    if (isDuplicate) {
      dispatch(toast('info', 'This combination is already added.'));
      return;
    }

    setEntries([
      ...entries,
      { duesCycle: cycle, duesOptions: newOptions, paymentStatus },
    ]);
    setCycle('');
    setOptionsValue([ANY_OPTION_VALUE]);
    setPaymentStatus('');
  };

  const handleRemove = index => {
    setEntries([...entries.slice(0, index), ...entries.slice(index + 1)]);
  };

  return (
    <Container>
      {(entries || []).map((entry, index) => {
        const entryOptions =
          entry.duesOptions === ANY_OPTION_VALUE ? [] : entry.duesOptions;
        return (
          <EntryBlock key={index} hasOptionRows={!!entryOptions.length}>
            <Wrap>
              <FitContent>
                {index > 0 ? <Space>or</Space> : <Space>-</Space>} Dues Cycle
              </FitContent>
              <Bold>{cycleNameByGuid[entry.duesCycle] || entry.duesCycle}</Bold>
              <FitContent>
                with {entry.paymentStatus === 'unpaid' ? 'an' : 'a'}
              </FitContent>
              <Bold>{entry.paymentStatus}</Bold>
              <FitContent>balance and options:</FitContent>
              {!entryOptions.length && <Bold>Any</Bold>}
              <CancelOutlined
                fontSize="small"
                style={{
                  fill: '#E63946',
                  // The "Any" Bold brings its own 10px side margins; without
                  // it the icon sits flush against "options:".
                  marginLeft: entryOptions.length ? 10 : 0,
                }}
                onClick={() => handleRemove(index)}
              />
            </Wrap>
            {entryOptions.map((guid, optionIndex) => (
              <OptionRow key={guid} tightenTop={optionIndex === 0}>
                <Bold>{optionNameByGuid[guid] || guid}</Bold>
              </OptionRow>
            ))}
          </EntryBlock>
        );
      })}
      <Content>
        <Text>{entries.length ? 'or' : ''} Dues Cycle</Text>
        <DuesCycleSelector
          value={cycle}
          onChange={setCycle}
          options={cyclesList}
          loading={loading}
          disabled={inputsDisabled}
        />
      </Content>
      <Content>
        <Text>with options</Text>
        <DuesOptionSelector
          value={optionsValue}
          onChange={setOptionsValue}
          options={optionsList}
          loading={loading}
          disabled={inputsDisabled}
        />
      </Content>
      {pickedOptions.map(guid => (
        <PickedOptionRow key={guid}>
          <Text />
          <Bold>{optionNameByGuid[guid] || guid}</Bold>
          <CancelOutlined
            fontSize="small"
            style={{ fill: '#E63946' }}
            onClick={() => handleRemoveOption(guid)}
          />
        </PickedOptionRow>
      ))}
      <Content>
        <Text>and</Text>
        <SelectDropDown
          value={(() => {
            if (!paymentStatus) return '';
            return paymentStatus === 'unpaid' ? 'Unpaid' : 'Paid';
          })()}
          menuItemNames={['Paid', 'Unpaid']}
          onChange={handlePaymentStatusChange}
        />
        <Text>balance</Text>
      </Content>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddCircleOutline />}
        disabled={inputsDisabled}
        style={{
          borderRadius: 50,
          backgroundColor: inputsDisabled ? '#9bc7ef' : '#007ADE',
          margin: '0 25px',
          padding: '5px 15px',
          color: 'white',
        }}
        onClick={handleAdd}
      >
        Add
      </Button>
    </Container>
  );
};

export default BalanceByDuesCycle;
